import type { ComboboxData, ComboboxItemGroup } from '@any-ui-react/core';
import groupBy from 'lodash-es/groupBy';
import map from 'lodash-es/map';
import omit from 'lodash-es/omit';
import { type ForwardedRef, forwardRef, useEffect, useMemo } from 'react';

import { type ListCompaniesQuery, type ListCompaniesQueryVariables, useListCompaniesQuery } from '@/common/graphql';

import type { ComboboxItemWithFormatted, CommonProps, UseQueryReturn } from '../props';
import { SelectMultiple, type SelectMultipleProps } from '../SelectMultiple';
import { SelectSingle, type SelectSingleProps } from '../SelectSingle';

export type CompanySelectVariables = Partial<Omit<ListCompaniesQueryVariables, 'pageNumber' | 'pageSize'>>;
type Data = ComboboxItemWithFormatted &
  Omit<ListCompaniesQuery['listCompanies']['items'][number], 'id' | 'name' | 'enabled'>;
type Props = CommonProps<CompanySelectVariables>;

const useQuery = (
  variables: CompanySelectVariables | undefined,
  onLoadingCompleted?: () => void,
): UseQueryReturn<ListCompaniesQueryVariables, Data> => {
  const { data, ...rest } = useListCompaniesQuery({
    variables: {
      pageNumber: null,
      pageSize: null,
      enabled: variables?.enabled ?? null,
      filterOnlyAccessible: variables?.filterOnlyAccessible ?? null,
      hasEmployees: variables?.hasEmployees ?? null,
      hasParentCompany: variables?.hasParentCompany ?? null,
      hasChildCompanies: variables?.hasChildCompanies ?? null,
      onlyIds: variables?.onlyIds ?? null,
      excludeIds: variables?.excludeIds ?? null,
      countryIds: variables?.countryIds ?? null,
    },
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    if (onLoadingCompleted) onLoadingCompleted();
  }, [onLoadingCompleted]);

  return {
    data:
      data?.listCompanies?.items?.map(it => ({
        ...it,
        value: it.id,
        label: it.name,
        disabled: !it.enabled,
        countryId: it.countryId,
      })) || [],
    ...rest,
  };
};

const useGroupedData = (flatData: Data[]): ComboboxData =>
  useMemo(
    () =>
      map(
        groupBy(flatData, 'countryId'),
        items =>
          ({
            group: items[0].country.name,
            items,
          }) as ComboboxItemGroup,
      ),
    [flatData],
  );

export const CompanySelectSingle = forwardRef(
  (props: Props & SelectSingleProps<Data>, ref: ForwardedRef<HTMLDivElement>) => {
    const { data: flatData, loading } = useQuery(props.variables, props.onLoadingCompleted);
    const grouped = useGroupedData(flatData);
    return <SelectSingle ref={ref} {...props} data={grouped as unknown as Data[]} loading={loading} />;
  },
);

export const CompanySelectMultiple = forwardRef(
  (props: Props & SelectMultipleProps<Data>, ref: ForwardedRef<HTMLDivElement>) => {
    const { data: flatData, loading } = useQuery(props.variables, props.onLoadingCompleted);
    const grouped = useGroupedData(flatData);
    return (
      <div ref={ref} className={props.className}>
        <SelectMultiple
          multipleSelectedLabel={length => `${length} companies`}
          {...omit(props, 'className')}
          data={grouped as unknown as Data[]}
          loading={loading}
        />
      </div>
    );
  },
);
